/**
 * G2eGente - Criar/Editar Pesquisa de Clima
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { climateSurveyAPI, employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Play,
  Loader2,
  ThermometerSun,
  FileText,
  Users,
  HelpCircle,
  Database,
  Upload,
  Download,
  FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const QUESTION_TYPES = [
  { value: 'escala_1_5', label: 'Escala 1-5 (Discordo totalmente → Concordo totalmente)' },
  { value: 'escala_1_10', label: 'Escala 1-10 (NPS)' },
  { value: 'sim_nao', label: 'Sim / Não' },
  { value: 'texto', label: 'Texto livre' },
  { value: 'multipla_escolha', label: 'Múltipla escolha' }
];

const SEGMENTATION_TYPES = [
  { value: 'todos', label: 'Todos os funcionários' },
  { value: 'departamentos', label: 'Por departamento' },
  { value: 'cargos', label: 'Por cargo' }
];

export default function ClimateSurveyForm() {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const isEditing = Boolean(surveyId);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [cargos, setCargos] = useState([]);
  
  // Banco de perguntas
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [questionBank, setQuestionBank] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [selectedBankQuestions, setSelectedBankQuestions] = useState([]);
  const [bankQuestionType, setBankQuestionType] = useState('escala_1_5');
  const [loadingBank, setLoadingBank] = useState(false);
  
  // Importação
  const [showImportModal, setShowImportModal] = useState(false);
  const [importQuestionType, setImportQuestionType] = useState('escala_1_5');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [form, setForm] = useState({
    titulo: '',
    descricao: '',
    perguntas: [
      { id: uuidv4(), texto: '', tipo: 'escala_1_5', obrigatoria: true, opcoes: null }
    ],
    segmentacao: { tipo: 'todos', valores: null },
    data_inicio: '',
    data_fim: ''
  });

  useEffect(() => {
    fetchDepartmentsAndCargos();
    fetchCategories();
    if (isEditing) {
      fetchSurvey();
    }
  }, [surveyId]);

  const fetchDepartmentsAndCargos = async () => {
    try {
      const [deptRes, empRes] = await Promise.all([
        employeesAPI.getDepartments(),
        employeesAPI.list()
      ]);
      setDepartments(deptRes.data.departments || []);
      
      // Extrair cargos únicos
      const uniqueCargos = [...new Set(empRes.data.map(e => e.cargo).filter(Boolean))];
      setCargos(uniqueCargos.sort());
    } catch (error) {
      console.error('Erro ao carregar departamentos/cargos');
    }
  };
  
  const fetchCategories = async () => {
    try {
      const { data } = await climateSurveyAPI.questionBank.getCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias');
    }
  };
  
  const fetchQuestionBank = async () => {
    setLoadingBank(true);
    try {
      const params = selectedCategory !== 'todas' ? { categoria: selectedCategory } : {};
      const { data } = await climateSurveyAPI.questionBank.list(params);
      setQuestionBank(data || []);
    } catch (error) {
      toast.error('Erro ao carregar banco de perguntas');
    } finally {
      setLoadingBank(false);
    }
  };
  
  const handleOpenQuestionBank = () => {
    setShowQuestionBank(true);
    setSelectedBankQuestions([]);
    fetchQuestionBank();
  };
  
  const handleToggleBankQuestion = (questionId) => {
    setSelectedBankQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };
  
  const handleAddFromBank = () => {
    const questionsToAdd = questionBank
      .filter(q => selectedBankQuestions.includes(q.id))
      .map(q => ({
        id: uuidv4(),
        texto: q.texto,
        tipo: bankQuestionType,
        obrigatoria: true,
        opcoes: null,
        categoria: q.categoria
      }));
    
    if (questionsToAdd.length === 0) {
      toast.error('Selecione pelo menos uma pergunta');
      return;
    }
    
    setForm(prev => ({
      ...prev,
      perguntas: [...prev.perguntas, ...questionsToAdd]
    }));
    
    toast.success(`${questionsToAdd.length} pergunta(s) adicionada(s)`);
    setShowQuestionBank(false);
    setSelectedBankQuestions([]);
  };
  
  const handleDownloadTemplate = async () => {
    try {
      const response = await climateSurveyAPI.questionBank.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_perguntas_clima.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template baixado');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };
  
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const { data } = await climateSurveyAPI.questionBank.importCSV(file);
      setImportResult(data);
      
      if (data.imported > 0) {
        toast.success(`${data.imported} pergunta(s) importada(s) ao banco`);
        // Recarregar banco se modal estiver aberto
        fetchQuestionBank();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fetchSurvey = async () => {
    try {
      const { data } = await climateSurveyAPI.getById(surveyId);
      setForm({
        titulo: data.titulo || '',
        descricao: data.descricao || '',
        perguntas: data.perguntas || [],
        segmentacao: data.segmentacao || { tipo: 'todos', valores: null },
        data_inicio: data.data_inicio || '',
        data_fim: data.data_fim || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar pesquisa');
      navigate('/company/climate-surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setForm(prev => ({
      ...prev,
      perguntas: [
        ...prev.perguntas,
        { id: uuidv4(), texto: '', tipo: 'escala_1_5', obrigatoria: true, opcoes: null }
      ]
    }));
  };

  const handleRemoveQuestion = (index) => {
    if (form.perguntas.length === 1) {
      toast.error('A pesquisa deve ter pelo menos uma pergunta');
      return;
    }
    setForm(prev => ({
      ...prev,
      perguntas: prev.perguntas.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      perguntas: prev.perguntas.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleSegmentationChange = (tipo) => {
    setForm(prev => ({
      ...prev,
      segmentacao: { tipo, valores: tipo === 'todos' ? null : [] }
    }));
  };

  const handleSegmentationValuesChange = (valor) => {
    setForm(prev => {
      const currentValues = prev.segmentacao.valores || [];
      const newValues = currentValues.includes(valor)
        ? currentValues.filter(v => v !== valor)
        : [...currentValues, valor];
      return {
        ...prev,
        segmentacao: { ...prev.segmentacao, valores: newValues }
      };
    });
  };

  const handleSave = async (activate = false) => {
    if (!form.titulo.trim()) {
      toast.error('Informe o título da pesquisa');
      return;
    }

    const validQuestions = form.perguntas.filter(q => q.texto.trim());
    if (validQuestions.length === 0) {
      toast.error('Adicione pelo menos uma pergunta');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        perguntas: validQuestions.map(q => ({
          ...q,
          id: q.id || uuidv4()
        }))
      };

      if (isEditing) {
        await climateSurveyAPI.update(surveyId, payload);
        if (activate) {
          await climateSurveyAPI.update(surveyId, { status: 'ativa' });
          toast.success('Pesquisa salva e ativada!');
        } else {
          toast.success('Pesquisa salva');
        }
      } else {
        const { data } = await climateSurveyAPI.create(payload);
        if (activate) {
          await climateSurveyAPI.update(data.id, { status: 'ativa' });
          toast.success('Pesquisa criada e ativada!');
        } else {
          toast.success('Pesquisa criada como rascunho');
        }
      }
      navigate('/company/climate-surveys');
    } catch (error) {
      toast.error('Erro ao salvar pesquisa');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/company/climate-surveys')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ThermometerSun className="w-7 h-7 text-cyan-600" />
            {isEditing ? 'Editar Pesquisa' : 'Nova Pesquisa de Clima'}
          </h1>
          <p className="text-slate-500">Configure as perguntas e segmentação</p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-cyan-600" />
              Informações Básicas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="titulo">Título / Tema da Pesquisa *</Label>
              <Input
                id="titulo"
                value={form.titulo}
                onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ex: Pesquisa de Clima 2024 - 1º Semestre"
                data-testid="survey-titulo"
              />
            </div>
            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Textarea
                id="descricao"
                value={form.descricao}
                onChange={(e) => setForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva o objetivo da pesquisa..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_inicio">Data de início (opcional)</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  value={form.data_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, data_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="data_fim">Data de término (opcional)</Label>
                <Input
                  id="data_fim"
                  type="date"
                  value={form.data_fim}
                  onChange={(e) => setForm(prev => ({ ...prev, data_fim: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Segmentation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-cyan-600" />
              Quem deve responder?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Segmentação</Label>
              <Select 
                value={form.segmentacao.tipo} 
                onValueChange={handleSegmentationChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENTATION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.segmentacao.tipo === 'departamentos' && departments.length > 0 && (
              <div>
                <Label className="mb-2 block">Selecione os departamentos:</Label>
                <div className="flex flex-wrap gap-2">
                  {departments.map(dept => (
                    <Badge
                      key={dept}
                      variant={form.segmentacao.valores?.includes(dept) ? 'default' : 'outline'}
                      className={`cursor-pointer ${form.segmentacao.valores?.includes(dept) ? 'bg-cyan-600' : ''}`}
                      onClick={() => handleSegmentationValuesChange(dept)}
                    >
                      {dept}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {form.segmentacao.tipo === 'cargos' && cargos.length > 0 && (
              <div>
                <Label className="mb-2 block">Selecione os cargos:</Label>
                <div className="flex flex-wrap gap-2">
                  {cargos.map(cargo => (
                    <Badge
                      key={cargo}
                      variant={form.segmentacao.valores?.includes(cargo) ? 'default' : 'outline'}
                      className={`cursor-pointer ${form.segmentacao.valores?.includes(cargo) ? 'bg-cyan-600' : ''}`}
                      onClick={() => handleSegmentationValuesChange(cargo)}
                    >
                      {cargo}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-lg">
                <HelpCircle className="w-5 h-5 text-cyan-600" />
                Perguntas
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenQuestionBank}
                  data-testid="open-question-bank-btn"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Banco de Perguntas
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImportModal(true)}
                  data-testid="open-import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.perguntas.map((pergunta, index) => (
              <div 
                key={pergunta.id} 
                className="p-4 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 text-slate-400 pt-2">
                    <GripVertical className="w-4 h-4" />
                    <span className="font-semibold">{index + 1}.</span>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={pergunta.texto}
                        onChange={(e) => handleQuestionChange(index, 'texto', e.target.value)}
                        placeholder="Digite a pergunta..."
                        data-testid={`question-text-${index}`}
                        className="flex-1"
                      />
                      {pergunta.categoria && (
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {pergunta.categoria}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex-1 min-w-[200px]">
                        <Select 
                          value={pergunta.tipo} 
                          onValueChange={(v) => handleQuestionChange(index, 'tipo', v)}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {QUESTION_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={pergunta.obrigatoria}
                          onCheckedChange={(v) => handleQuestionChange(index, 'obrigatoria', v)}
                        />
                        <Label className="text-sm">Obrigatória</Label>
                      </div>
                    </div>
                    
                    {/* Multiple choice options */}
                    {pergunta.tipo === 'multipla_escolha' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Opções (uma por linha)</Label>
                        <Textarea
                          value={pergunta.opcoes?.join('\n') || ''}
                          onChange={(e) => handleQuestionChange(index, 'opcoes', e.target.value.split('\n').filter(Boolean))}
                          placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                          rows={3}
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRemoveQuestion(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              onClick={handleAddQuestion}
              className="w-full border-dashed"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Pergunta Manual
            </Button>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/company/climate-surveys')}>
            Cancelar
          </Button>
          <Button 
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Rascunho
          </Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => handleSave(true)}
            disabled={saving}
            data-testid="save-and-activate-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Salvar e Ativar
          </Button>
        </div>
      </div>
      
      {/* Modal: Banco de Perguntas */}
      <Dialog open={showQuestionBank} onOpenChange={setShowQuestionBank}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-600" />
              Banco de Perguntas
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Label className="text-sm mb-1 block">Filtrar por categoria</Label>
              <Select 
                value={selectedCategory} 
                onValueChange={(v) => { setSelectedCategory(v); setTimeout(fetchQuestionBank, 100); }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-sm mb-1 block">Tipo de resposta para selecionadas</Label>
              <Select value={bankQuestionType} onValueChange={setBankQuestionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto border rounded-lg p-2 min-h-[300px]">
            {loadingBank ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : questionBank.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <FolderOpen className="w-12 h-12 mb-2 text-slate-300" />
                <p>Nenhuma pergunta no banco</p>
                <p className="text-sm">Importe perguntas via CSV para começar</p>
              </div>
            ) : (
              <div className="space-y-2">
                {questionBank.map(q => (
                  <div 
                    key={q.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedBankQuestions.includes(q.id) 
                        ? 'bg-cyan-50 border-cyan-300' 
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                    onClick={() => handleToggleBankQuestion(q.id)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        checked={selectedBankQuestions.includes(q.id)}
                        onCheckedChange={() => handleToggleBankQuestion(q.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm">{q.texto}</p>
                        <Badge variant="outline" className="text-xs mt-1">{q.categoria}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-slate-500">
                {selectedBankQuestions.length} pergunta(s) selecionada(s)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowQuestionBank(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAddFromBank}
                  disabled={selectedBankQuestions.length === 0}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Selecionadas
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal: Importar Perguntas */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-cyan-600" />
              Importar Perguntas
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Importe perguntas de um arquivo CSV para adicionar ao banco de perguntas da empresa.
              As perguntas importadas ficarão disponíveis para uso em qualquer pesquisa.
            </p>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Baixar Template
              </Button>
            </div>
            
            <Separator />
            
            <div>
              <Label className="mb-2 block">Selecione o arquivo CSV</Label>
              <Input 
                type="file" 
                accept=".csv"
                ref={fileInputRef}
                onChange={handleImportFile}
                disabled={importing}
              />
              <p className="text-xs text-slate-500 mt-1">
                Formato: categoria;pergunta (separado por ponto e vírgula)
              </p>
            </div>
            
            {importing && (
              <div className="flex items-center gap-2 text-cyan-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Importando...</span>
              </div>
            )}
            
            {importResult && (
              <div className={`p-3 rounded-lg ${importResult.imported > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                <p className="font-medium">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-sm">
                    <p className="font-medium">Erros:</p>
                    <ul className="list-disc list-inside">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportResult(null); }}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
